import {
  useOpenCloseTransitionStates,
  type OpenCloseTransitionStateEffects,
} from "../useOpenCloseTransitionStates";

/**
 * Effects configuration for dialog transition states
 * @property {() => void} [onOpenAnimationStart] - Callback when opening animation starts
 * @property {() => void} [onCloseAnimationStart] - Callback when closing animation starts
 * @property {() => void} [onOpenAnimationComplete] - Callback when opening animation completes
 * @property {() => void} [onCloseAnimationComplete] - Callback when closing animation completes
 */

/**
 * Custom hook for managing popover transition states and animations.
 *
 * Features:
 * - Manages popover open/close transition states
 * - Handles animation timing based on CSS animation duration
 * - Provides callbacks for animation lifecycle events
 * - Supports controlled popover state management
 * - Automatically handles showPopover() and closePopover() calls
 * - Provides utility flags for open/closed states
 *
 * @param popover - The popover element
 * @param openProp - The open prop controlling popover visibility
 * @param options - Additional, optional options including callbacks for animation start, complete, and initialization
 * @returns Object containing popover state, control functions, and utility flags
 */
export const usePopoverTransitionStates = (
  popover: HTMLElement | undefined,
  openProp: boolean | undefined,
  options: OpenCloseTransitionStateEffects = {},
) => {
  const { onOpenAnimationStart, onCloseAnimationComplete } = options;
  return useOpenCloseTransitionStates(openProp, {
    ...options,
    onOpenAnimationStart: () => {
      if (!popover) return;
      onOpenAnimationStart?.();
    },
    onCloseAnimationComplete: () => {
      if (!popover) return;
      onCloseAnimationComplete?.();
    },
    durationMs: () => {
      if (!popover || !window) return 0;
      const duration = window.getComputedStyle(popover).animationDuration;
      return parseFloat(duration) * 1000;
    },
  });
};
